import { Link } from "react-router-dom";
import { motion } from "framer-motion";

function SantharionLogo({ size = 36 }) {
  const s = size, c = s / 2;
  const outerR = s * 0.44, innerR = s * 0.20, pts = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    pts.push(`${(c + r * Math.cos(a)).toFixed(2)},${(c + r * Math.sin(a)).toFixed(2)}`);
  }
  return (
    <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`} fill="none">
      <defs>
        <linearGradient id="pgLogo" x1="0" y1="0" x2={s} y2={s} gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#f97316"/><stop offset="50%" stopColor="#ec4899"/><stop offset="100%" stopColor="#8b5cf6"/>
        </linearGradient>
      </defs>
      <polygon points={pts.join(" ")} fill="url(#pgLogo)"/>
    </svg>
  );
}

const Section = ({ title, children }) => (
  <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }}
    viewport={{ once:true }} className="mb-10">
    <h2 className="text-xl font-bold mb-3" style={{ color:"#f97316" }}>{title}</h2>
    <div className="text-sm leading-relaxed space-y-3" style={{ color:"#9ca3af" }}>{children}</div>
  </motion.div>
);

function PrivacyPolicy() {
  const lastUpdated = "March 2, 2026";

  return (
    <div className="min-h-screen text-white" style={{ background:"#080410", fontFamily:"'Sora',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&display=swap');`}</style>

      {/* Nav */}
      <header className="px-6 py-4 flex items-center justify-between"
        style={{ borderBottom:"1px solid rgba(249,115,22,0.12)" }}>
        <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition">
          <SantharionLogo size={36}/>
          <div>
            <p className="font-black text-sm"
              style={{ background:"linear-gradient(135deg,#f97316,#ec4899,#8b5cf6)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
              Santharion AI
            </p>
            <p className="text-xs" style={{ color:"#4b5563" }}>by LearnHubAI</p>
          </div>
        </Link>
        <Link to="/" className="text-xs px-4 py-2 rounded-xl transition hover:text-orange-400"
          style={{ color:"#6b7280", border:"1px solid rgba(255,255,255,0.08)" }}>
          ← Back to Home
        </Link>
      </header>

      {/* Hero */}
      <div className="max-w-3xl mx-auto px-6 pt-16 pb-8 text-center">
        <motion.div initial={{ opacity:0, y:-20 }} animate={{ opacity:1, y:0 }}>
          <span className="inline-block text-xs font-bold px-3 py-1.5 rounded-full mb-4"
            style={{ background:"rgba(249,115,22,0.1)", color:"#f97316", border:"1px solid rgba(249,115,22,0.25)" }}>
            Legal Document
          </span>
          <h1 className="text-4xl md:text-5xl font-black mb-4">
            Privacy{" "}
            <span style={{ background:"linear-gradient(135deg,#f97316,#ec4899)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
              Policy
            </span>
          </h1>
          <p className="text-sm" style={{ color:"#6b7280" }}>Last updated: {lastUpdated}</p>
        </motion.div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 pb-24">
        <div className="rounded-2xl p-8 md:p-10"
          style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(249,115,22,0.12)" }}>

          <Section title="1. Introduction">
            <p>
              Welcome to <strong style={{ color:"#f3f4f6" }}>Santharion AI</strong>, an email automation
              service provided by <strong style={{ color:"#f3f4f6" }}>LearnHubAI</strong>. This Privacy
              Policy explains how we collect, use, and protect your information when you use our service
              at <strong style={{ color:"#f3f4f6" }}>learnhubai.com</strong>.
            </p>
            <p>
              By using Santharion AI, you agree to the practices described in this policy.
              If you do not agree, please do not use our service.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p>When you sign in with Google, we receive the following from Google OAuth:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Your <strong style={{ color:"#f3f4f6" }}>name</strong> and <strong style={{ color:"#f3f4f6" }}>email address</strong></li>
              <li>Your <strong style={{ color:"#f3f4f6" }}>profile picture</strong> (optional, from Google)</li>
              <li>A temporary <strong style={{ color:"#f3f4f6" }}>OAuth access token</strong> to send emails on your behalf</li>
            </ul>
            <p>We also collect anonymized <strong style={{ color:"#f3f4f6" }}>visitor statistics</strong> (page visit counts) to understand site usage. No personally identifiable information is stored for analytics.</p>
          </Section>

          <Section title="3. How We Use Your Information">
            <ul className="list-disc pl-5 space-y-1">
              <li>To authenticate you via Google Sign-In</li>
              <li>To send emails <strong style={{ color:"#f3f4f6" }}>on your behalf</strong> only when you explicitly request it</li>
              <li>To display your name and email in the app interface</li>
              <li>To improve our service through anonymized usage data</li>
            </ul>
            <p>We <strong style={{ color:"#f3f4f6" }}>do not</strong> use your information for advertising, marketing, or sale to third parties.</p>
          </Section>

          <Section title="4. Google OAuth & Gmail Access">
            <p>
              Santharion AI uses Google OAuth 2.0 to access your Gmail account.
              Specifically, we request the <code style={{ color:"#fb923c", background:"rgba(249,115,22,0.1)", padding:"1px 5px", borderRadius:4 }}>gmail.send</code> scope,
              which allows us to <strong style={{ color:"#f3f4f6" }}>send emails from your Gmail account only when you ask us to</strong>.
            </p>
            <p>
              <strong style={{ color:"#f3f4f6" }}>We never:</strong>
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Read, store, or access your existing emails</li>
              <li>Send emails without your explicit instruction</li>
              <li>Store your OAuth token in our database — it exists only in your browser session</li>
              <li>Share your token or email access with any third party</li>
            </ul>
            <p>
              Your access token is stored only in your browser's <code style={{ color:"#fb923c", background:"rgba(249,115,22,0.1)", padding:"1px 5px", borderRadius:4 }}>sessionStorage</code> and
              is automatically deleted when you close the browser tab.
            </p>
          </Section>

          <Section title="5. Data Storage & Retention">
            <p>
              <strong style={{ color:"#f3f4f6" }}>We do not store your OAuth token or email content in our database.</strong> Once your session ends, all authentication data is deleted from your browser automatically.
            </p>
            <p>
              We store anonymized visitor counts in our database to track site usage. No personal information is associated with these counts.
            </p>
          </Section>

          <Section title="6. Data Sharing">
            <p>We do not sell, trade, or share your personal information with third parties, except:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong style={{ color:"#f3f4f6" }}>Google</strong> — to authenticate you and send emails via Gmail API</li>
              <li><strong style={{ color:"#f3f4f6" }}>Groq AI</strong> — your email prompts are sent to Groq's API to generate email templates. No personal identifying information is included beyond what you type.</li>
            </ul>
          </Section>

          <Section title="7. Security">
            <p>
              We implement appropriate technical measures to protect your information:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>OAuth tokens are never stored server-side</li>
              <li>All communication uses HTTPS</li>
              <li>We do not store passwords — authentication is handled entirely by Google</li>
            </ul>
          </Section>

          <Section title="8. Your Rights">
            <p>You have the right to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong style={{ color:"#f3f4f6" }}>Revoke access</strong> — Go to <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer" style={{ color:"#f97316", textDecoration:"underline" }}>myaccount.google.com/permissions</a> and remove LearnHubAI / Santharion AI</li>
              <li><strong style={{ color:"#f3f4f6" }}>Sign out</strong> — Use the sign-out button in the app to clear your session</li>
              <li><strong style={{ color:"#f3f4f6" }}>Contact us</strong> — Request deletion of any data we hold about you</li>
            </ul>
          </Section>

          <Section title="9. Children's Privacy">
            <p>
              Santharion AI is not directed to children under the age of 13.
              We do not knowingly collect personal information from children.
              If you believe a child has provided us information, please contact us and we will delete it.
            </p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. We will notify users by updating the
              "Last updated" date at the top of this page. Continued use of the service after changes
              constitutes acceptance of the updated policy.
            </p>
          </Section>

          <Section title="11. Contact Us">
            <p>If you have questions about this Privacy Policy, please contact:</p>
            <div className="rounded-xl p-4 mt-2" style={{ background:"rgba(249,115,22,0.06)", border:"1px solid rgba(249,115,22,0.2)" }}>
              <p><strong style={{ color:"#f3f4f6" }}>Gurucharan P</strong> — Founder, LearnHubAI</p>
              <p>📧 <a href="mailto:cguru203@gmail.com" style={{ color:"#f97316" }}>cguru203@gmail.com</a></p>
              <p>📞 <a href="tel:9740043604" style={{ color:"#f97316" }}>9740043604</a></p>
              <p className="mt-2"><strong style={{ color:"#f3f4f6" }}>Harish G</strong> — Co-Founder, LearnHubAI</p>
              <p>📞 <a href="tel:9731337072" style={{ color:"#f97316" }}>9731337072</a></p>
            </div>
          </Section>

        </div>

        {/* Footer note */}
        <p className="text-center text-xs mt-8" style={{ color:"#374151" }}>
          © 2026 Santharion AI · LearnHubAI · All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
